import { useEffect, useRef, useState } from 'react';
import { X, ShieldAlert } from 'lucide-react';
import useBoardStore from '../../store/boardStore';
import useUserSearch from '../../hooks/useUserSearch';
import { dialog } from '../../store/dialogStore';
import { ACCESS_LEVELS, ACCESS_SOURCE_LABEL, DEFAULT_GRANT_LEVEL } from '../../constants/access';

function Avatar({ user, size = 8 }) {
  if (user?.avatar_url) {
    return <img src={user.avatar_url} className={`w-${size} h-${size} rounded-full object-cover shrink-0`} alt={user.name} />;
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold shrink-0`}>
      {user?.name?.[0]?.toUpperCase() ?? '?'}
    </div>
  );
}

export default function InviteMembersModal({ onClose }) {
  // Seletores individuais — nunca assina a store inteira (evita re-render a
  // cada tique de polling de outros pedacos do board).
  const currentBoard = useBoardStore((s) => s.currentBoard);
  const boardAccess = useBoardStore((s) => s.boardAccess);
  const loadingBoardAccess = useBoardStore((s) => s.loadingBoardAccess);
  const boardAccessError = useBoardStore((s) => s.boardAccessError);
  const fetchBoardAccess = useBoardStore((s) => s.fetchBoardAccess);
  const setBoardAccessLevel = useBoardStore((s) => s.setBoardAccessLevel);
  const clearBoardAccessOverride = useBoardStore((s) => s.clearBoardAccessOverride);
  const setBoardVisibility = useBoardStore((s) => s.setBoardVisibility);

  const [accessDenied, setAccessDenied] = useState(false);
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [rowErrors, setRowErrors] = useState({});
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityError, setVisibilityError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);

  const {
    search, setSearch, results, hasMore, searching, searchError,
    handleShowMore, handleRetrySearch,
  } = useUserSearch({ excludeBoardId: currentBoard?.id });

  const boardId = currentBoard?.id;

  useEffect(() => {
    if (!boardId) return;
    setAccessDenied(false);
    fetchBoardAccess(boardId).catch((err) => {
      if (err?.status === 403) setAccessDenied(true);
    });
    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  useEffect(() => { setActiveIndex(-1); }, [search, results.length]);

  const existingIds = new Set(boardAccess.map((a) => a.user.id));
  const visibleResults = results.filter((u) => !existingIds.has(u.id));

  const markSaving = (userId, on) => {
    setSavingIds((s) => {
      const next = new Set(s);
      if (on) next.add(userId); else next.delete(userId);
      return next;
    });
  };

  const clearRowError = (userId) => {
    setRowErrors((e) => {
      if (!(userId in e)) return e;
      const next = { ...e };
      delete next[userId];
      return next;
    });
  };

  const handleAdd = async (person) => {
    if (!boardId || savingIds.has(person.id)) return;
    markSaving(person.id, true);
    clearRowError(person.id);
    try {
      await setBoardAccessLevel(boardId, person.id, DEFAULT_GRANT_LEVEL);
      setSearch('');
    } catch (err) {
      setRowErrors((e) => ({ ...e, [person.id]: err.message || 'Falha ao adicionar' }));
    } finally {
      markSaving(person.id, false);
    }
  };

  const handleLevelChange = async (entry, level) => {
    if (!boardId) return;
    const userId = entry.user.id;
    markSaving(userId, true);
    clearRowError(userId);
    try {
      await setBoardAccessLevel(boardId, userId, level);
    } catch (err) {
      setRowErrors((e) => ({ ...e, [userId]: err.message || 'Falha ao salvar' }));
    } finally {
      markSaving(userId, false);
    }
  };

  const handleResetToDefault = async (entry) => {
    if (!boardId) return;
    const userId = entry.user.id;
    markSaving(userId, true);
    clearRowError(userId);
    try {
      await clearBoardAccessOverride(boardId, userId);
    } catch (err) {
      setRowErrors((e) => ({ ...e, [userId]: err.message || 'Falha ao voltar ao padrão' }));
    } finally {
      markSaving(userId, false);
    }
  };

  const handleVisibilityChange = async (visibility) => {
    if (!boardId) return;
    if (visibility === 'private') {
      const confirmed = await dialog.confirm(
        'Tornar o quadro privado',
        'Quem tem acesso só pela área de trabalho ou pelo grupo perde o acesso. Continua só quem tem exceção neste quadro.',
        { confirmLabel: 'Tornar privado', danger: true }
      );
      if (!confirmed) return;
    }
    setVisibilitySaving(true);
    setVisibilityError(null);
    try {
      await setBoardVisibility(boardId, visibility);
    } catch (err) {
      setVisibilityError(err.message || 'Falha ao salvar visibilidade');
    } finally {
      setVisibilitySaving(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (search) setSearch(''); else onClose();
      return;
    }
    if (!visibleResults.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visibleResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = visibleResults[activeIndex >= 0 ? activeIndex : 0];
      if (target) handleAdd(target);
    }
  };

  const handleModalKeyDown = (e) => {
    if (e.key === 'Escape' && document.activeElement !== inputRef.current) {
      onClose();
    }
  };

  const visibility = currentBoard?.visibility || 'inherit';
  const isPrivate = visibility === 'private';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-[#1e2235] border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleModalKeyDown}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h2 className="text-base font-semibold text-text-primary">Acesso ao quadro</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-text-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {accessDenied ? (
          <div className="p-6 text-center">
            <ShieldAlert className="w-8 h-8 text-danger mx-auto mb-3" />
            <p className="text-sm text-text-primary mb-1">Sem permissão para gerenciar o acesso deste quadro.</p>
            <p className="text-xs text-text-secondary">Apenas o dono do quadro ou um admin da organização pode ver esta tela.</p>
          </div>
        ) : (
          <div className="p-4 space-y-4 overflow-y-auto">
            <div className="border border-border/50 rounded-lg p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-text-primary font-medium">Visibilidade do quadro</p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    {isPrivate
                      ? 'Privado — só entra quem tem exceção de acesso listada abaixo.'
                      : 'Segue a área de trabalho — herda o acesso de quem está na área.'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {visibilitySaving && <div className="w-3.5 h-3.5 border-2 border-accent border-t-transparent rounded-full animate-spin" />}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isPrivate}
                    disabled={visibilitySaving}
                    onClick={() => handleVisibilityChange(isPrivate ? 'inherit' : 'private')}
                    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                      isPrivate ? 'bg-accent' : 'bg-white/10'
                    }`}
                    title={isPrivate ? 'Privado' : 'Segue a área de trabalho'}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                        isPrivate ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
              {visibilityError && <p className="text-xs text-danger mt-2">{visibilityError}</p>}
            </div>

            <div className="relative">
              <input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Adicionar pessoa por nome ou e-mail..."
                className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary placeholder-text-secondary/50 outline-none focus:border-accent/50"
              />
              {search.trim() && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl z-10 max-h-64 overflow-y-auto">
                  {visibleResults.map((u, idx) => (
                    <button
                      key={u.id}
                      onClick={() => handleAdd(u)}
                      disabled={savingIds.has(u.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors text-left ${
                        idx === activeIndex ? 'bg-white/10' : 'hover:bg-white/5'
                      }`}
                    >
                      <Avatar user={u} size={7} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-text-primary truncate">{u.name}</div>
                        <div className="text-xs text-text-secondary truncate">{u.email}</div>
                      </div>
                      {savingIds.has(u.id) && <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin shrink-0" />}
                    </button>
                  ))}

                  {searching && visibleResults.length === 0 && (
                    <div className="px-3 py-2.5 text-xs text-text-secondary">Buscando...</div>
                  )}

                  {searchError && (
                    <div className="flex items-center justify-between gap-2 px-3 py-2.5 text-xs text-danger">
                      <span className="truncate">{searchError}</span>
                      <button onClick={handleRetrySearch} className="shrink-0 text-accent hover:underline">
                        Tentar de novo
                      </button>
                    </div>
                  )}

                  {!searching && !searchError && visibleResults.length === 0 && (
                    <div className="px-3 py-2.5 text-xs text-text-secondary">Nenhum resultado</div>
                  )}

                  {hasMore && !searchError && (
                    <button
                      onClick={handleShowMore}
                      disabled={searching}
                      className="w-full px-3 py-2 text-xs text-accent hover:bg-white/5 transition-colors disabled:opacity-50"
                    >
                      {searching ? 'Carregando...' : 'Mostrar mais'}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-medium text-text-secondary mb-2">Quem tem acesso</p>

              {loadingBoardAccess && boardAccess.length === 0 && (
                <p className="text-xs text-text-secondary text-center py-3">Carregando...</p>
              )}

              {boardAccessError && (
                <div className="flex items-center justify-between gap-2 text-xs text-danger py-2">
                  <span className="truncate">{boardAccessError}</span>
                  <button onClick={() => fetchBoardAccess(boardId)} className="text-accent hover:underline shrink-0">
                    Tentar de novo
                  </button>
                </div>
              )}

              <div className="space-y-1">
                {boardAccess.map((entry) => {
                  const isException = entry.source === 'board';
                  const isDeny = entry.level === 'deny';
                  const saving = savingIds.has(entry.user.id);
                  const rowError = rowErrors[entry.user.id];
                  return (
                    <div key={entry.user.id}>
                      <div
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                          isException ? 'border-border/50 bg-white/[0.02]' : 'border-transparent'
                        }`}
                      >
                        <Avatar user={entry.user} size={8} />
                        <div className={`flex-1 min-w-0 ${!isException ? 'opacity-60' : ''}`}>
                          <div className={`text-sm truncate ${isException ? 'text-text-primary' : 'text-text-secondary italic'}`}>
                            {entry.user.name}
                          </div>
                          <div className="text-xs text-text-secondary truncate">
                            {ACCESS_SOURCE_LABEL[entry.source] || entry.source}
                          </div>
                        </div>
                        {saving && <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin shrink-0" />}
                        <select
                          value={entry.level}
                          disabled={saving}
                          onChange={(e) => handleLevelChange(entry, e.target.value)}
                          className={`text-xs border rounded px-1.5 py-1 outline-none focus:border-accent/50 cursor-pointer shrink-0 disabled:opacity-50 ${
                            isDeny
                              ? 'bg-danger/10 text-danger border-danger/30'
                              : isException
                              ? 'bg-accent/10 text-accent border-accent/30 font-medium'
                              : 'bg-transparent text-text-secondary border-border/50'
                          }`}
                        >
                          {ACCESS_LEVELS.map((l) => (
                            <option key={l.value} value={l.value}>{l.label}</option>
                          ))}
                        </select>
                        {isException && (
                          <button
                            onClick={() => handleResetToDefault(entry)}
                            disabled={saving}
                            className="text-xs text-text-secondary hover:text-accent transition-colors shrink-0 disabled:opacity-50"
                            title="Voltar ao padrão herdado"
                          >
                            Voltar ao padrão
                          </button>
                        )}
                      </div>
                      {rowError && <p className="text-xs text-danger px-3 pt-0.5">{rowError}</p>}
                    </div>
                  );
                })}

                {!loadingBoardAccess && boardAccess.length === 0 && !boardAccessError && (
                  <p className="text-xs text-text-secondary text-center py-2">Ninguém tem acesso a este quadro ainda.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
